import { useEffect, useId, useState } from 'react';
import { useIntl } from 'react-intl';
import { LoadingSkeleton, Selector, useQueryFetch } from 'sonata-react-components';
import { getInsertionOrder, getInsertionOrders } from 'sonata-static-storage/lib/entities';
import { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';
import { InsertionOrder } from 'sonata-static-storage/lib/schemas/multiFlight/insertionOrder.schema';

import { useGlobalStore } from '@/store/global';
import { getSessionByKeys } from '@/store/global/session/selectors.ts';

type Props = {
  cpId?: CampaignPlan['_id'];
  id: string;
  onChange: (io: InsertionOrder) => void;
  onLoading?: () => void;
};

type State = {
  insertionOrderSelected?: {
    id: string;
    label: string;
  };
};

const InsertionOrderSelector = ({ cpId, id, onChange, onLoading }: Props) => {
  const { formatMessage } = useIntl();
  const uuidIOList = useId();
  const uuidIOData = useId();
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));
  const [{ insertionOrderSelected }, setState] = useState<State>({ insertionOrderSelected: undefined });

  const { data, isLoading } = useQueryFetch(
    ['insertionOrderSelector', uuidIOList, cpId],
    async () =>
      getInsertionOrders(userId, {
        ...(cpId && { campaignPlans: [cpId] }),
        fields: ['_id', 'name'],
        sortBy: 'updated',
        sortOrder: 'desc',
        unlimited: 'true'
      }),
    {
      enabled: !!cpId
    }
  );

  const ioList: Pick<InsertionOrder, '_id' | 'name'>[] | undefined = data?.items.map(i => i.toJSON());

  const { data: ioData, isFetching: ioDataLoading } = useQueryFetch(
    ['insertionOrderSelector', uuidIOData, insertionOrderSelected?.id],
    async () => getInsertionOrder(userId, insertionOrderSelected?.id || ''),
    {
      enabled: !!insertionOrderSelected,
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    if (ioData) {
      onChange(ioData.toJSON());
    }
  }, [ioData]);

  useEffect(() => {
    if (ioDataLoading) {
      onLoading?.();
    }
  }, [ioDataLoading]);

  const handleCampaignPlanChange = (value: { id: string; label: string }) => {
    setState(prevState => ({
      ...prevState,
      insertionOrderSelected: value
    }));
  };

  if (isLoading || !ioList || !cpId) {
    return <LoadingSkeleton className="insertionOrderSelector" height={40} width="100%" />;
  }
  return (
    <Selector
      className="insertionOrderSelector"
      id={id}
      key={`insertionOrderSelector_${cpId}`}
      label={formatMessage({
        defaultMessage: 'Insertion Order:',
        id: 'campaignPlans.modal.ioSelectorLabel'
      })}
      onChange={handleCampaignPlanChange}
      options={ioList.map(c => ({
        id: c._id as string,
        label: c.name
      }))}
      placeholder={formatMessage({
        defaultMessage: 'Select Insertion Order',
        id: 'campaignPlans.insertionOrderSelector.placeholder'
      })}
      type="simple"
    />
  );
};

InsertionOrderSelector.displayName = 'InsertionOrderSelector';

export default InsertionOrderSelector;
